import React, { memo } from 'react';
import Image from 'next/image';
import Button from '@components/atoms/Button';
import ModalManager from '../../molecules/ModalManager';
import styles from './CarFinance.module.scss';
import type { BankData } from '@interfaces/CarFinance.types';
import { FINANCE_CONSTANTS, MODAL_CONFIG, MODAL_TYPES } from '@constants/carFinance';

interface FinanceModalProps {
  isOpen: boolean;
  type: "downpayment" | "tenure" | "interest" | null;
  editMode: "downpayment" | "tenure" | "interest" | undefined;
  onClose: () => void;
  onUpdate: () => void;
  // Down payment props
  downPaymentInputValue: string;
  downPaymentError: string;
  onDownPaymentChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  // Tenure props
  tempLoanTenure: number;
  onTenureChange: (year: number) => void;
  // Interest props
  bankArray: BankData[];
  tempSelectedBank: BankData | null;
  interestInputValue: string;
  interestError: string;
  onBankChange: (bank: BankData) => void;
  onInterestChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  // Validation
  isUpdateDisabled: boolean;
}

export const FinanceModal = memo(({
  isOpen,
  type,
  editMode,
  onClose,
  onUpdate,
  downPaymentInputValue,
  downPaymentError,
  onDownPaymentChange,
  tempLoanTenure,
  onTenureChange,
  bankArray,
  tempSelectedBank,
  interestInputValue,
  interestError,
  onBankChange,
  onInterestChange,
  isUpdateDisabled,
}: FinanceModalProps) => {
  return (
    <ModalManager
      isOpen={isOpen}
      onClose={onClose}
      title={
        type === MODAL_TYPES.DOWNPAYMENT
          ? MODAL_CONFIG[MODAL_TYPES.DOWNPAYMENT].title
          : type === MODAL_TYPES.TENURE
          ? MODAL_CONFIG[MODAL_TYPES.TENURE].title
          : MODAL_CONFIG[MODAL_TYPES.INTEREST].title
      }
      customClass={styles.modalManagerContent}
      modalHeaderClass={styles.modalManagerHeader}
      modalBodyClass={styles.modalManagerBody}
    >
      <div className={styles.modalContentWrapper}>
        {/* Downpayment input */}
        {type === MODAL_TYPES.DOWNPAYMENT && (
          <div
            className={`${styles.inputWrapper} ${
              editMode === MODAL_TYPES.DOWNPAYMENT ? "" : styles.isMobileHidden
            }`}
          >
            <label className={styles.label} htmlFor="downPaymentInput">
              {MODAL_CONFIG[MODAL_TYPES.DOWNPAYMENT].inputLabel}
            </label>
            <span className={styles.rupeePrefix}>₹</span>
            <input
              id="downPaymentInput"
              type="text"
              value={downPaymentInputValue}
              onChange={onDownPaymentChange}
              placeholder={MODAL_CONFIG[MODAL_TYPES.DOWNPAYMENT].placeholder}
              className={`${styles.inputField} ${
                downPaymentError ? styles.inputError : styles.inputNormal
              }`}
              min={0}
            />
            {downPaymentError && (
              <div className={styles.errorText}>{downPaymentError}</div>
            )}
          </div>
        )}

        {/* Loan Tenure buttons */}
        {type === MODAL_TYPES.TENURE && (
          <div
            className={`${styles.tenureButtonsWrapper} ${
              editMode === MODAL_TYPES.TENURE ? "" : styles.isMobileHidden
            }`}
          >
            <div className={styles.tenureButtonsGrid}>
              {FINANCE_CONSTANTS.TENURE_OPTIONS.map((year) => (
                <button
                  key={year}
                  className={`${styles.tenureButton} ${
                    tempLoanTenure === year ? styles.tenureButtonSelected : ""
                  }`}
                  onClick={() => onTenureChange(year)}
                >
                  {year} {year === 1 ? FINANCE_CONSTANTS.YEAR : FINANCE_CONSTANTS.YEARS}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Partnered banks */}
        {type === MODAL_TYPES.INTEREST && (
          <>
            <div
              className={`${styles.banksButtonsWrapper} ${
                editMode === MODAL_TYPES.INTEREST ? "" : styles.isMobileHidden
              }`}
            >
              <div className={styles.banksButtonsGrid}>
                {bankArray.map((bank) => {
                  const isSelected = tempSelectedBank?.id === bank.id;
                  return (
                    <button
                      key={bank.id}
                      className={`${styles.bankButton} ${
                        isSelected ? styles.bankButtonSelected : ""
                      }`}
                      onClick={() => onBankChange(bank)}
                    >
                      {bank.logo_image && (
                        <Image
                          src={bank.logo_image}
                          alt={bank.provider_name}
                          width={40}
                          height={40}
                          className={styles.bankLogo}
                          loading="lazy"
                        />
                      )}
                      <div className={styles.bankButtonContent}>
                        <div className={styles.bankName}>
                          {bank.provider_name}
                        </div>
                        <div className={styles.bankInterest}>
                          {parseFloat(String(bank.interest_rate))}%
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div
              className={`${styles.inputWrapper} ${
                editMode === MODAL_TYPES.INTEREST ? "" : styles.isMobileHidden
              }`}
            >
              <label className={styles.label} htmlFor="otherInterestInput">
                {MODAL_CONFIG[MODAL_TYPES.INTEREST].inputLabel}
              </label>
              <span className={styles.rupeePrefix}>{MODAL_CONFIG[MODAL_TYPES.INTEREST].inputPrefix}</span>
              <input
                id="otherInterestInput"
                type="text"
                value={interestInputValue}
                placeholder={MODAL_CONFIG[MODAL_TYPES.INTEREST].placeholder}
                onChange={onInterestChange}
                className={`${styles.inputField} ${
                  interestError ? styles.inputError : styles.inputNormal
                }`}
                min={0}
                step="0.01"
              />
              {interestError && (
                <div className={styles.errorText}>{interestError}</div>
              )}
            </div>
          </>
        )}

        <Button
          className={styles.compareButton}
          disabled={isUpdateDisabled}
          onClick={onUpdate}
        >
        {MODAL_CONFIG.BUTTON_TEXT}
        </Button>
      </div>
    </ModalManager>
  );
});

FinanceModal.displayName = 'FinanceModal';
